import * as React from "react";
import {
  Alert,
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import { getProyectosApiBaseUrl } from "../../utils/proyectosApiBaseUrl";
import type { BurdenMedidorHomologado } from "../../utils/burdenCalculo";

const getToken = () => {
  try {
    return atob(localStorage.getItem("authToken") || "") || "";
  } catch {
    return "";
  }
};

type Props = {
  medidores: BurdenMedidorHomologado[];
  onChange: () => void;
  /** Sin borde de panel cuando va dentro del modal. */
  embedded?: boolean;
};

export default function BurdenMedidoresAdmin({ medidores, onChange, embedded = false }: Props) {
  const [error, setError] = React.useState<string | null>(null);
  const [ok, setOk] = React.useState<string | null>(null);
  const [guardando, setGuardando] = React.useState(false);
  const [nuevo, setNuevo] = React.useState({
    nombre: "",
    cto_corriente_va: "",
    cto_tension_va: "",
  });
  const [editDraft, setEditDraft] = React.useState<Record<string, Partial<BurdenMedidorHomologado>>>({});

  const crear = async () => {
    try {
      setGuardando(true);
      setError(null);
      setOk(null);
      const res = await fetch(`${getProyectosApiBaseUrl()}/burden/medidores`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({
          nombre: nuevo.nombre.trim(),
          cto_corriente_va: Number(String(nuevo.cto_corriente_va).replace(",", ".")),
          cto_tension_va: Number(String(nuevo.cto_tension_va).replace(",", ".")),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "No se pudo crear el medidor.");
      setNuevo({ nombre: "", cto_corriente_va: "", cto_tension_va: "" });
      setOk("Medidor agregado.");
      onChange();
    } catch (e: any) {
      setError(e.message || "Error al crear.");
    } finally {
      setGuardando(false);
    }
  };

  const guardarFila = async (m: BurdenMedidorHomologado) => {
    const d = editDraft[m.id];
    if (!d) return;
    try {
      setGuardando(true);
      setError(null);
      const res = await fetch(`${getProyectosApiBaseUrl()}/burden/medidores/${m.id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify(d),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "No se pudo actualizar.");
      setEditDraft((prev) => {
        const copy = { ...prev };
        delete copy[m.id];
        return copy;
      });
      setOk("Medidor actualizado.");
      onChange();
    } catch (e: any) {
      setError(e.message || "Error al actualizar.");
    } finally {
      setGuardando(false);
    }
  };

  const eliminar = async (m: BurdenMedidorHomologado) => {
    if (!window.confirm(`¿Eliminar «${m.nombre}» del listado homologado?`)) return;
    try {
      setGuardando(true);
      setError(null);
      const res = await fetch(`${getProyectosApiBaseUrl()}/burden/medidores/${m.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "No se pudo eliminar.");
      setOk("Medidor eliminado.");
      onChange();
    } catch (e: any) {
      setError(e.message || "Error al eliminar.");
    } finally {
      setGuardando(false);
    }
  };

  const val = (m: BurdenMedidorHomologado, field: keyof BurdenMedidorHomologado) => {
    const d = editDraft[m.id];
    if (d && d[field] !== undefined) return d[field];
    return m[field];
  };

  const contenido = (
    <>
      {!embedded && (
        <>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
            Administración de medidores homologados EMCALI
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
            Agregar, modificar o eliminar medidores y sus valores CTO corriente / tensión (VA).
          </Typography>
        </>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}
      {ok && (
        <Alert severity="success" sx={{ mb: 1 }} onClose={() => setOk(null)}>
          {ok}
        </Alert>
      )}
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>
              <strong>Medidor</strong>
            </TableCell>
            <TableCell align="right">
              <strong>CTO corriente (VA)</strong>
            </TableCell>
            <TableCell align="right">
              <strong>CTO tensión (VA)</strong>
            </TableCell>
            <TableCell align="right">
              <strong>Acciones</strong>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {medidores.map((m) => (
            <TableRow key={m.id}>
              <TableCell>
                <TextField
                  size="small"
                  fullWidth
                  value={String(val(m, "nombre") ?? "")}
                  onChange={(e) =>
                    setEditDraft((prev) => ({
                      ...prev,
                      [m.id]: { ...prev[m.id], nombre: e.target.value },
                    }))
                  }
                />
              </TableCell>
              <TableCell align="right">
                <TextField
                  size="small"
                  type="number"
                  inputProps={{ step: 0.001, min: 0 }}
                  value={val(m, "cto_corriente_va")}
                  onChange={(e) =>
                    setEditDraft((prev) => ({
                      ...prev,
                      [m.id]: { ...prev[m.id], cto_corriente_va: Number(e.target.value) },
                    }))
                  }
                  sx={{ width: 100 }}
                />
              </TableCell>
              <TableCell align="right">
                <TextField
                  size="small"
                  type="number"
                  inputProps={{ step: 0.001, min: 0 }}
                  value={val(m, "cto_tension_va")}
                  onChange={(e) =>
                    setEditDraft((prev) => ({
                      ...prev,
                      [m.id]: { ...prev[m.id], cto_tension_va: Number(e.target.value) },
                    }))
                  }
                  sx={{ width: 100 }}
                />
              </TableCell>
              <TableCell align="right">
                <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                  {editDraft[m.id] && (
                    <Button size="small" disabled={guardando} onClick={() => void guardarFila(m)}>
                      Guardar
                    </Button>
                  )}
                  <IconButton
                    size="small"
                    color="error"
                    aria-label="Eliminar medidor"
                    disabled={guardando}
                    onClick={() => void eliminar(m)}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Box sx={{ mt: 2 }}>
        <Typography variant="caption" fontWeight={700} display="block" sx={{ mb: 1 }}>
          Agregar medidor
        </Typography>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ md: "center" }}>
          <TextField
            size="small"
            label="Nombre medidor"
            value={nuevo.nombre}
            onChange={(e) => setNuevo((p) => ({ ...p, nombre: e.target.value }))}
            sx={{ flex: 2 }}
          />
          <TextField
            size="small"
            label="CTO corriente"
            type="number"
            inputProps={{ step: 0.001, min: 0 }}
            value={nuevo.cto_corriente_va}
            onChange={(e) => setNuevo((p) => ({ ...p, cto_corriente_va: e.target.value }))}
            sx={{ width: 120 }}
          />
          <TextField
            size="small"
            label="CTO tensión"
            type="number"
            inputProps={{ step: 0.001, min: 0 }}
            value={nuevo.cto_tension_va}
            onChange={(e) => setNuevo((p) => ({ ...p, cto_tension_va: e.target.value }))}
            sx={{ width: 120 }}
          />
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            disabled={guardando || !nuevo.nombre.trim()}
            onClick={() => void crear()}
          >
            Agregar
          </Button>
        </Stack>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
        Total en listado: {medidores.length} medidor(es).
      </Typography>
    </>
  );

  if (embedded) {
    return <Box>{contenido}</Box>;
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: "#fffef8" }}>
      {contenido}
    </Paper>
  );
}
